import React, { useCallback, useState, useEffect } from 'react';
import { Grid, InputBase, IconButton, Switch, Checkbox } from '@material-ui/core';
import { TextField } from '@material-ui/core';
import {
  verifyDuplicateSchemaLabel,
  verifyDuplicateSchemaCriterionNumber,
} from '../../services/validation/validation.service';
import ActionButtons from './ActionButtons';
import EditButton from './EditButton.component';
import {
  createExamScoringSchema,
  modifyExamScoringSchema,
  modifyExamScoringSchemaForcedComments,
} from '../../services/exams/sessions/sessions.service';
import { DetailsProps } from './DetailsProps.types';
import CloseIcon from '@material-ui/icons/Close';
import './style.css';
import useExamSessionValidation from '../../hooks/useExamSessionValidation';
import DeleteIcon from '@material-ui/icons/Delete';
import { generateUUid } from '../../utils/generator';
import {
  ISchemeDetails,
  Score,
  IScoreDetails,
  IIdResult,
  examScoreValueCommentRequired,
  Task,
  ExamScoringSchemaResponse,
} from '../../models/session.types';
import { AxiosError, AxiosResponse } from 'axios';
import { WorkMode, NewTreeItemId } from '../SessionTree/SessionTree.types';
import { BreadcrumbLevel } from '../DialogBreadcrumbs/DialogBreadcrumbs.types';
import { useLayoutContext } from '../../Context/Layout.context';
import { ButtonOne } from '../ButtonOne/ButtonOne.component';
import { useStyles as sessionDetails } from './SessionDetails.styles';
import { useStyles } from './SchemeScoresDetails.styles';
import DeleteButton from './DeleteButton.component';
import { LeavePrompt } from '../Prompt/LeavePrompt/LeavePrompt.component';
import { EditSchemeScoresDetailsErrorPrompt } from './EditSchemeScoresDetailsErrorPrompt';
import TooltipOne from '../TooltipOne/TooltipOne.component';
import SwitchOne from '../SwitchOne/SwitchOne.component';
import { Prompt } from '../Prompt/Prompt.component';
import { blockIconForInput, blockIconForInputWithOptions } from './BlockItemForInput';
import hasValueChanged from '../../utils/objectsComparer';

interface SchemeScoresDetailsProps extends DetailsProps {
  data: ISchemeDetails;
  activeTreeLevel: string;
  isReadOnly: () => boolean;
  onDelete: () => void;
  isSioePkz: boolean;
}

const SchemeScoresDetails: React.FC<SchemeScoresDetailsProps> = (props) => {
  const classes = useStyles();
  const sessionDetailsClasses = sessionDetails();

  const [schemeNumber, setSchemeNumber] = useState<string>(props.data ? props.data.number : '');
  const [schemeLabel, setSchemeLabel] = useState<string>(props.data ? props.data.label : '');
  const [schemeName, setSchemeName] = useState<string>(props.data ? props.data.name : '');
  const [schemeScores, setSchemeScores] = useState<Score[]>(props.data ? props.data.scores : []);
  const [isFieldLabelEdited, setIsFieldLabelEdited] = useState<boolean>(false);
  const [isFieldNumberEdited, setIsFieldNumberEdited] = useState<boolean>(false);
  const [isBlurLabel, setIsBlurLabel] = useState<boolean>(false);
  const [isBlurNumber, setIsBlurNumber] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [pointsFrom, setPointsFrom] = useState<string>('');
  const [pointsTo, setPointsTo] = useState<string>('');
  const [gradation, setGradation] = useState<string>('');
  const [canGenerateScores, setCanGenerateScores] = useState<boolean>(false);
  const [scoreHasChange, setScoreHasChange] = useState<boolean>(false);
  const [scoreIsValid, setScoreIsValid] = useState<boolean>(true);
  const [inputsListValid, setInputListValid] = useState<boolean>(false);
  const [isValidSchemeName, setIsValidSchemeName] = useState<boolean>(true);
  const [ValidationTextForSchemaName, setValidationTextForSchemaName] = useState<string>('');
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [isZeroScoreValidationError, setIsZeroScoreValidationError] = useState<boolean>(false);
  const [isEditSchemeScoresDetailsErrorPromptOpen, setIsEditSchemeScoresDetailsErrorPromptOpen] =
    useState<boolean>(false);
  const [isPromptOpen, setIsPromptOpen] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [isCommentRequiredEnabled, setIsCommentRequiredEnabled] = useState<boolean>(
    props.data.isCommentRequiredEnabled ?? false,
  );
  const [examScoreValueCommentRequiredList, setExamScoreValueCommentRequiredList] = useState<
    examScoreValueCommentRequired[]
  >([]);
  const { isValidValue: isValidSchemeNumber } = useExamSessionValidation({
    isEdited: isFieldNumberEdited,
    isAdd: props.workMode === WorkMode.ADD,
    inputValue: !schemeNumber ? schemeNumber : schemeNumber.trim(),
    data: props.data.number,
    verificationApi: useCallback(
      (value: string) => verifyDuplicateSchemaCriterionNumber(props.data.gradeRuleId, value),
      [props.data.gradeRuleId],
    ),
    onBlur: isBlurNumber,
  });
  const { openAlert } = useLayoutContext();
  const { isValidValue: isValidSchemeLabel } = useExamSessionValidation({
    isEdited: isFieldLabelEdited && props.data.label !== schemeLabel,
    isAdd: props.workMode === WorkMode.ADD,
    inputValue: !schemeLabel ? schemeLabel : schemeLabel.trim(),
    data: !props.data.label
      ? props.data.label
      : props.data.label !== undefined
      ? props.data.label.trim()
      : props.data.label,
    verificationApi: useCallback(
      (value: string) => verifyDuplicateSchemaLabel(props.data.gradeRuleId, value),
      [props.data.gradeRuleId],
    ),
    onBlur: isBlurLabel,
  });

  const handleSchemeScoresChange = (index: number, value: number) => {
    const newScores = [...schemeScores];
    newScores[index].value = value;
    setSchemeScores(newScores);
    setScoreIsValid(!schemeScores.length);
    setIsDisabled(false);
  };

  const handleSchemeScoreRemoval = useCallback(
    (index: number) => {
      setSchemeScores((prevState) => prevState.filter((p, i) => i !== index));
      setScoreHasChange(true);
      if (isValidSchemeLabel && isValidSchemeName && isValidSchemeNumber) {
        setIsDisabled(false);
      }
    },
    [schemeScores, scoreHasChange, scoreIsValid],
  );

  const handleError = (err: AxiosError) => {
    if (err.response) {
      if (err.response.data === ExamScoringSchemaResponse.MissingZero) {
        setIsZeroScoreValidationError(true);
        return;
      }
      openAlert('error');
    } else openAlert('error');
  };

  const saveChanges = () => {
    if (props.data.id !== NewTreeItemId.SchemeScores) {
      modifyExamScoringSchema(
        props.data.id,
        schemeLabel,
        schemeName,
        schemeNumber,
        schemeScores.map((score: Score) => score.value),
      )
        .then((result: AxiosResponse<IIdResult>) => {
          openAlert('success');
          props.setWorkMode(WorkMode.VIEW);
          props.onSave(result.data, false);
          setScoreHasChange(false);
          setIsPromptOpen(false);
        })
        .catch(handleError);
    } else {
      createExamScoringSchema(
        props.data.parentId,
        schemeLabel,
        schemeName,
        schemeNumber,
        schemeScores.map((score: Score) => score.value),
      )
        .then((result: AxiosResponse<IIdResult>) => {
          openAlert('success');
          props.setWorkMode(WorkMode.VIEW);
          props.onSave(result.data, true);
          setScoreHasChange(false);
          setIsPromptOpen(false);
        })
        .catch(handleError);
    }
  };

  const refreshScoreValueCommentRequiredList = () => {
    const scoreWithCommentRequired: examScoreValueCommentRequired[] = [];
    schemeScores.slice(0, schemeScores.length - 1).map((score) => {
      scoreWithCommentRequired.push({
        examScoreValueId: score.id,
        isCommentRequired: score.isCommentRequired ?? false,
      });
    });
    setIsLeavePromptOpen(false);
    setExamScoreValueCommentRequiredList(scoreWithCommentRequired);
  };

  const saveForcedCommentsChanges = () => {
    modifyExamScoringSchemaForcedComments(
      props.data.id,
      isCommentRequiredEnabled,
      examScoreValueCommentRequiredList,
    ).then(
      () => {
        openAlert('success');
        props.onSave({ id: props.data.id }, false);
        setIsDisabled(true);
        setIsDataChanged(false);
      },
      () => {
        openAlert('error');
      },
    );
  };

  useEffect(() => {
    if (
      isValidSchemeNumber &&
      isValidSchemeLabel &&
      isValidSchemeName &&
      !(schemeNumber === props.data.number && schemeLabel === props.data.label && schemeName === props.data.name) &&
      schemeNumber &&
      schemeLabel &&
      schemeName &&
      schemeNumber.trim() !== '' &&
      schemeLabel.trim() !== '' &&
      schemeName.trim() !== ''
    ) {
      setInputListValid(true);
    } else {
      setInputListValid(false);
    }
  }, [
    isValidSchemeNumber,
    isValidSchemeLabel,
    isValidSchemeName,
    inputsListValid,
    schemeLabel,
    schemeName,
    scoreHasChange,
    schemeNumber,
    props.data,
  ]);

  useEffect(() => {
    setIsDisabled(!(schemeScores.length && inputsListValid));
    if (
      schemeNumber &&
      schemeLabel &&
      schemeName &&
      isValidSchemeLabel &&
      isValidSchemeName &&
      isValidSchemeNumber &&
      schemeNumber?.trim() !== '' &&
      schemeLabel?.trim() !== '' &&
      schemeName?.trim() !== '' &&
      scoreHasChange &&
      schemeScores.length !== 0
    ) {
      setIsDisabled(false);
    }
  }, [scoreHasChange, inputsListValid, schemeNumber, schemeLabel, schemeName, schemeScores]);

  useEffect(() => {
    if (pointsFrom && pointsTo && parseFloat(gradation) > 0 && parseFloat(pointsFrom) < parseFloat(pointsTo)) {
      setCanGenerateScores(true);
    } else {
      setCanGenerateScores(false);
    }
  }, [gradation, pointsFrom, pointsTo]);

  const handleResetScoresInputs = () => {
    setPointsFrom('');
    setPointsTo('');
    setGradation('');
    setCanGenerateScores(false);
    setIsDisabled(true);
  };

  const revert = () => {
    const { number, label, name, scores } = props.data;
    setSchemeNumber(number);
    setSchemeLabel(label);
    setSchemeName(name);
    setSchemeScores(scores);
    setScoreIsValid(false);
    setScoreHasChange(false);
    setIsFieldLabelEdited(false);
    handleResetScoresInputs();
    setValidationTextForSchemaName('');
    setIsValidSchemeName(true);
    setIsDisabled(true);
  };

  const closeHandle = () => {
    props.workMode === WorkMode.VIEW
      ? setIsLeavePromptOpen(true)
      : props.onClose(props.data, BreadcrumbLevel.GRADERULES, revert);
  };

  const handleEditMode = () => {
    props.onEdit(props.data);
  };

  useEffect(() => {
    refreshScoreValueCommentRequiredList();
  }, [schemeScores]);

  const compareScores = (scores: Score[]) => {
    const scoresValues = scores.map((x: Score) => x.value);
    const oldScoresValues: Score[] = props.data.scores;

    if (!oldScoresValues) return true;

    return (
      oldScoresValues.filter((x) => !scoresValues.includes(x.value)).length !== 0 ||
      oldScoresValues.length !== scores.length
    );
  };
  const generateScores = () => {
    if (canGenerateScores && pointsFrom && pointsTo && gradation) {
      const scores: IScoreDetails[] = [];

      const numberPointsFrom = parseFloat(pointsFrom.toString());
      const numberPointsTo = parseFloat(pointsTo.toString());

      for (let index = numberPointsFrom; index <= numberPointsTo; index += parseFloat(gradation)) {
        const value = index.toFixed(2);
        scores.push({
          id: '',
          parentId: props.data.parentId,
          value: parseFloat(value),
          isCommentRequired: false,
        });
      }
      if (compareScores(scores)) {
        setSchemeScores(scores);
        setScoreHasChange(true);
      } else {
        setSchemeScores(props.data.scores);
        setScoreHasChange(false);
        setIsDisabled(true);
      }
    }
  };
  const deletedAllScores = () => {
    setSchemeScores([]);
    setIsDisabled(true);
    setScoreIsValid(false);
    setScoreHasChange(true);
  };

  const handleCommentForcedChange = useCallback(
    (id: string) => {
      const examScoreValueCommentRequiredCopy: examScoreValueCommentRequired[] = examScoreValueCommentRequiredList.map(
        (obj) => (obj.examScoreValueId === id ? { ...obj, isCommentRequired: !obj.isCommentRequired } : obj),
      );
      const propsData: examScoreValueCommentRequired[] = props.data.scores.map((x) => ({
        examScoreValueId: x.id,
        isCommentRequired: x.isCommentRequired,
      }));
      propsData.pop();
      const valueChanged = hasValueChanged(examScoreValueCommentRequiredCopy, propsData);
      setExamScoreValueCommentRequiredList(examScoreValueCommentRequiredCopy);
      setIsDataChanged(valueChanged);
      setIsDisabled(!valueChanged);
    },
    [examScoreValueCommentRequiredList, props.data.scores],
  );

  const isChecked = (id: string): boolean => {
    return examScoreValueCommentRequiredList.some((s) => s.examScoreValueId === id && s.isCommentRequired);
  };

  const handleOnClose = () => {
    setIsCommentRequiredEnabled(props.data.isCommentRequiredEnabled ?? false);
    setIsDisabled(true);
    setIsDataChanged(false);
    refreshScoreValueCommentRequiredList();
    setIsLeavePromptOpen(false);
  };

  const handlePromptClose = () => {
    closeHandle();
    setIsPromptOpen(false);
  };

  const schemeNumberWithError = props.workMode !== WorkMode.VIEW && !isValidSchemeNumber;
  const schemeLabelWithError = props.workMode !== WorkMode.VIEW && !isValidSchemeLabel;
  const schemeNameWithError = props.workMode !== WorkMode.VIEW && !isValidSchemeName;

  const handleValidationTextForSchemName = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>) => {
    if (e.target.value.trim().length > 512) {
      setValidationTextForSchemaName('​Przekroczono dopuszczalną liczbę znaków. Max. 512');
      setIsValidSchemeName(false);
    } else if (e.target.value.trim().length === 0) {
      setValidationTextForSchemaName('Pole "Nazwa" nie może być puste');
      setIsValidSchemeName(false);
    } else {
      setValidationTextForSchemaName('');
      setIsValidSchemeName(true);
    }
  };
  useEffect(() => {
    setIsDataChanged(isCommentRequiredEnabled != props.data.isCommentRequiredEnabled);
  }, [isCommentRequiredEnabled]);

  const handleSwithValueChange = () => {
    setIsCommentRequiredEnabled(!isCommentRequiredEnabled);
    setIsDisabled(false);
  };
  const editingDisabled =
    props.workMode === WorkMode.VIEW || props.data.isExamSheetConfigured || props.data.isUsedInCriteriaSetups;
  const editingCriterionNameDisabled = props.workMode === WorkMode.VIEW;

  return (
    <>
      {props.data && (
        <div style={{ maxHeight: '490px', overflowY: 'auto' }}>
          <Grid container alignContent="space-between" alignItems="flex-end" style={{ height: '490px' }}>
            <Grid container justifyContent="flex-end" alignContent="flex-end">
              {props.workMode !== WorkMode.EDIT ? (
                <EditButton
                  workMode={props.workMode}
                  handleEditMode={() =>
                    !props.data.isUsedInCriteriaSetups
                      ? handleEditMode()
                      : setIsEditSchemeScoresDetailsErrorPromptOpen(true)
                  }
                />
              ) : (
                <DeleteButton
                  workMode={props.workMode}
                  handleDeleteMode={props.onDelete}
                  isDisabled={props.isReadOnly()}
                />
              )}
            </Grid>
            <Grid
              container
              justifyContent="center"
              alignContent="flex-start"
              style={{
                transform: 'translateY(-60px)',
              }}
            >
              <Grid container justifyContent="center" alignContent="center" className={sessionDetailsClasses.headerRow}>
                {props.workMode === WorkMode.VIEW && (
                  <div className={sessionDetailsClasses.headerText}>Schemat punktowania</div>
                )}
                {props.workMode === WorkMode.EDIT && (
                  <div className={sessionDetailsClasses.headerText}>Edycja schematu punktowania</div>
                )}
                {props.workMode === WorkMode.ADD && (
                  <div className={sessionDetailsClasses.headerText}>Zdefiniuj schemat punktowania</div>
                )}
              </Grid>
              <Grid
                container
                justifyContent="center"
                alignContent="center"
                className={`${classes.formInputNarrowContainer} ${
                  schemeNumberWithError || schemeLabelWithError ? classes.formInputNarrowContainerWithError : ''
                }`}
              >
                <TextField
                  className={classes.formInputNarrow}
                  value={schemeNumber}
                  onChange={(e) => {
                    setSchemeNumber(e.target.value.trimEnd());
                    setIsFieldNumberEdited(true);
                    setIsBlurNumber(false);
                  }}
                  onBlur={() => setIsBlurNumber(true)}
                  id="Numer kryterium"
                  label="Numer kryterium*"
                  variant="outlined"
                  disabled={editingDisabled}
                  error={schemeNumberWithError}
                  inputProps={{
                    'aria-label': 'Numer kryterium',
                    style: {
                      padding: '15.5px',
                      color: props.isReadOnly() ? 'unset' : '#2C2D3E',
                    },
                  }}
                  InputProps={{
                    startAdornment: blockIconForInput(editingDisabled),
                  }}
                  InputLabelProps={{
                    style: { color: '#2C2D3E' },
                  }}
                  FormHelperTextProps={{
                    style: {
                      margin: '2px 0px 0px 0px',
                      fontSize: '10px',
                      color: '#BC0000',
                    },
                  }}
                  helperText={
                    schemeNumberWithError ? 'Pole "Numer kryterium" musi być unikatowe w obrębie zadania' : undefined
                  }
                />
                <TextField
                  className={classes.formInputNarrow}
                  value={schemeLabel}
                  onChange={(e) => {
                    setSchemeLabel(e.target.value.trimStart());
                    setIsFieldLabelEdited(true);
                    setIsBlurLabel(false);
                  }}
                  onBlur={() => setIsBlurLabel(true)}
                  id="Etykieta*"
                  label="Etykieta*"
                  variant="outlined"
                  disabled={editingDisabled}
                  error={schemeLabelWithError}
                  inputProps={{
                    'aria-label': 'Etykieta',
                    style: {
                      padding: '15.5px',
                      color: props.isReadOnly() ? 'unset' : '#2C2D3E',
                    },
                  }}
                  InputProps={{
                    startAdornment: blockIconForInput(editingDisabled),
                  }}
                  InputLabelProps={{
                    style: { color: '#2C2D3E' },
                  }}
                  FormHelperTextProps={{
                    style: {
                      margin: '2px 1px 0px 0px',
                      fontSize: '9px',
                      color: '#BC0000',
                    },
                  }}
                  helperText={schemeLabelWithError ? 'Pole "Etykieta" musi być unikatowe w obrębie zadania' : undefined}
                />
                <TextField
                  className={`${sessionDetailsClasses.formInput} ${
                    schemeNameWithError ? classes.formInputWithErrors : ''
                  }`}
                  value={schemeName}
                  onChange={(e) => {
                    setSchemeName(e.target.value.trimStart());
                  }}
                  onBlur={(e) => {
                    handleValidationTextForSchemName(e);
                  }}
                  id="Nazwa kryterium*"
                  label="Nazwa kryterium*"
                  variant="outlined"
                  disabled={editingCriterionNameDisabled}
                  error={schemeNameWithError}
                  inputProps={{
                    'aria-label': 'Nazwa kryterium',
                    style: {
                      padding: '15.5px',
                      color: props.isReadOnly() ? 'unset' : '#2C2D3E',
                    },
                  }}
                  InputProps={{
                    startAdornment: blockIconForInput(editingCriterionNameDisabled),
                  }}
                  InputLabelProps={{
                    style: { color: '#2C2D3E' },
                  }}
                  FormHelperTextProps={{
                    style: {
                      margin: '2px 1px 0px 0px',
                      fontSize: '9px',
                      color: '#BC0000',
                    },
                  }}
                  helperText={ValidationTextForSchemaName}
                />
              </Grid>
              <Grid container justifyContent="flex-start" style={{ width: '340px' }}>
                <div className={classes.subHeader}>Punktacja:</div>
              </Grid>
              {props.workMode !== WorkMode.VIEW && !editingDisabled && (
                <Grid container justifyContent="center" alignItems="center" direction="column">
                  <Grid container justifyContent="center" alignItems="center">
                    <Grid className={classes.formInputScore}>
                      <TextField
                        label="Punkty od"
                        variant="outlined"
                        type="number"
                        inputProps={{
                          'aria-label': 'Punkty od',
                          style: {
                            padding: '15.5px',
                            color: '#2C2D3E',
                          },
                          min: 0,
                        }}
                        value={pointsFrom}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => {
                          setPointsFrom(e.target.value.trimStart());
                        }}
                        disabled={editingDisabled}
                      />
                    </Grid>
                    <Grid className={classes.formInputScore}>
                      <TextField
                        label="Punkty do"
                        variant="outlined"
                        type="number"
                        inputProps={{
                          'aria-label': 'Punkty do',
                          style: {
                            padding: '15.5px',
                            color: '#2C2D3E',
                          },
                          min: 0,
                        }}
                        InputLabelProps={{ shrink: true }}
                        value={pointsTo}
                        onChange={(e) => setPointsTo(e.target.value.trimStart())}
                        disabled={editingDisabled}
                      />
                    </Grid>
                    <Grid className={classes.formInputScore}>
                      <TextField
                        label="Gradacja"
                        variant="outlined"
                        type="number"
                        inputProps={{
                          'aria-label': 'Gradacja',
                          style: {
                            padding: '15.5px',
                            color: '#2C2D3E',
                          },
                          min: 1,
                        }}
                        value={gradation}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setGradation(Math.abs(parseFloat(e.target.value.trimStart())).toString())}
                        disabled={editingDisabled}
                      />
                    </Grid>
                  </Grid>
                  <Grid container justifyContent="flex-end" style={{ width: '350px', margin: '10px' }}>
                    <ButtonOne
                      variant="outlined"
                      color="primary"
                      onClick={generateScores}
                      disabled={!canGenerateScores}
                    >
                      GENERUJ
                    </ButtonOne>
                  </Grid>
                </Grid>
              )}
              {schemeScores && schemeScores.length > 0 && (
                <div
                  className={
                    props.workMode !== WorkMode.VIEW && !editingDisabled
                      ? classes.formInputScoreContainerBackGround
                      : classes.formInputScoreContainer
                  }
                  style={{
                    height: '75px',
                  }}
                >
                  <div style={{ maxHeight: props.workMode !== WorkMode.VIEW ? '78px' : '40px' }}>
                    {schemeScores.map((score: Score, index: number) => (
                      <div key={generateUUid(score.id)} className={classes.scoreContainer}>
                        <InputBase
                          id="Punkty"
                          value={score.value}
                          disabled={true}
                          style={{
                            textAlignLast: 'center',
                          }}
                          type="text"
                          inputProps={{
                            min: 0,
                            'aria-label': 'Punkty',
                          }}
                          startAdornment={blockIconForInputWithOptions({
                            isDisabled: true,
                            iconStyle: {
                              marginRight: props.workMode === WorkMode.EDIT ? '-8px' : '-16px',
                              marginLeft: '4px',
                            },
                          })}
                          onChange={(e) => handleSchemeScoresChange(index, parseFloat(e.target.value.trimStart()))}
                        />
                        {props.workMode !== WorkMode.VIEW && !editingDisabled && (
                          <CloseIcon
                            aria-label={`Usuń punktację o wartości ${score.value}`}
                            style={{
                              color: 'rgba(44, 45, 62, 0.8)',
                              cursor: 'pointer',
                              fontSize: '16px',
                              margin: '8px 2px',
                            }}
                            onClick={() => {
                              handleSchemeScoreRemoval(index);
                            }}
                          />
                        )}
                      </div>
                    ))}
                    <IconButton
                      disableRipple
                      aria-label="Usuń całą punktację"
                      style={{
                        display: props.workMode !== WorkMode.VIEW && !editingDisabled ? 'block' : 'none',
                        float: 'right',
                      }}
                      onClick={deletedAllScores}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              )}

              {props.workMode === WorkMode.VIEW && !props.isSioePkz && (
                <Grid
                  container
                  className={classes.formInputScoreContainer}
                  style={{
                    width: '350px',
                    maxHeight: '85px',
                    overflow: 'visible',
                  }}
                >
                  <hr className={classes.line}></hr>
                  <Grid container justifyContent="space-between" direction="row" alignItems="center">
                    <div className={classes.subHeader} style={{ margin: '5px 0px' }}>
                      Wymuś komentarz
                    </div>
                    <TooltipOne
                      title={
                        isCommentRequiredEnabled
                          ? 'W tym kryterium egzaminator będzie musiał wprowadzać obowiązkowy komentarz dla wskazanej punktacji'
                          : 'W tym kryterium egzaminator nie będzie musiał wprowadzać obowiązkowych komentarzy'
                      }
                      placement="top"
                    >
                      <span>
                        <SwitchOne
                          disableRipple
                          checked={isCommentRequiredEnabled}
                          className={classes.switch}
                          color={'primary'}
                          inputProps={{
                            'aria-label': 'Wymuś komentarz',
                          }}
                          onChange={handleSwithValueChange}
                        />
                      </span>
                    </TooltipOne>
                  </Grid>
                  <Grid container justifyContent="flex-start">
                    <div className={classes.subHeaderParagraph}>
                      Wybierz punkty, dla których chcesz wymusić komentarz
                    </div>
                  </Grid>
                </Grid>
              )}

              {schemeScores && schemeScores.length > 0 && (
                <Grid
                  container
                  className={
                    props.workMode !== WorkMode.VIEW && !editingDisabled
                      ? classes.formInputScoreContainerBackGround
                      : classes.formInputScoreContainer
                  }
                >
                  {props.workMode === WorkMode.VIEW && (
                    <Grid container style={{ maxHeight: '78px' }}>
                      <Grid container style={{ marginLeft: '-10px' }}>
                        {(isCommentRequiredEnabled ||
                          examScoreValueCommentRequiredList.some((s) => s.isCommentRequired)) &&
                          schemeScores.slice(0, schemeScores.length - 1).map((score: Score) => (
                            <div key={score.id} className={classes.schemeScoreWithCheckbox}>
                              <Checkbox
                                disableRipple
                                className={classes.checkbox}
                                color={'primary'}
                                onChange={() => handleCommentForcedChange(score.id)}
                                checked={isChecked(score.id)}
                                disabled={!isCommentRequiredEnabled && examScoreValueCommentRequiredList.length > 0}
                                inputProps={{
                                  'aria-label': 'Punkty',
                                }}
                              />
                              <div className={classes.scoreContainer}>
                                <InputBase
                                  id="Punkty"
                                  value={score.value}
                                  disabled={true}
                                  startAdornment={blockIconForInput(true, { marginRight: '-15px', marginLeft: '5px' })}
                                  style={{
                                    textAlignLast: 'center',
                                  }}
                                  type="text"
                                  inputProps={{
                                    'aria-label': 'Punkty',
                                  }}
                                  onChange={undefined}
                                />
                              </div>
                            </div>
                          ))}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>

            <Grid
              container
              justifyContent="flex-end"
              style={{
                transform: 'translateY(-75px)',
              }}
            >
              {(props.workMode !== WorkMode.VIEW || isDataChanged) && (
                <ActionButtons
                  disable={isDisabled}
                  saveHandle={
                    props.workMode === WorkMode.VIEW
                      ? saveForcedCommentsChanges
                      : props.data.isExamSheetConfigured
                      ? () => setIsPromptOpen(true)
                      : saveChanges
                  }
                  closeHandle={closeHandle}
                  activeTreeLevel={props.activeTreeLevel}
                />
              )}
            </Grid>
          </Grid>
        </div>
      )}
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={handleOnClose} onCancel={() => setIsLeavePromptOpen(false)} />
      <EditSchemeScoresDetailsErrorPrompt
        open={isEditSchemeScoresDetailsErrorPromptOpen}
        onClose={() => {
          setIsEditSchemeScoresDetailsErrorPromptOpen(false),
          handleEditMode()
        }}
      />
      <Prompt
        type="confirmation"
        open={isPromptOpen}
        size="sm"
        onCancel={() => handlePromptClose()}
        onConfirm={() => saveChanges()}
        mainActionButtonText="TAK"
        mainText={<>Czy na pewno chcesz zmienić nazwę kryterium?</>}
        subTexts={[
          {
            text: 'Arkusz został już skonfigurowany. Wprowadzona zmiana będzie widoczna dla wszystkich użytkowników.',
          },
        ]}
      />
      {isZeroScoreValidationError && (
        <Prompt
          type="error"
          open={isZeroScoreValidationError}
          onConfirm={() => setIsZeroScoreValidationError(false)}
          subTexts={[
            {
              text: 'Nie możesz zapisać schematu oceniania. W tym zadaniu opcja "Brak odpowiedzi" jest dostępna dla egzaminatora - w schemacie oceniania brakuje punktacji 0.',
            },
          ]}
        />
      )}
    </>
  );
};

export default SchemeScoresDetails;
